import {CellRangeEventArgs, FlexGrid, FormatItemEventArgs, HitTestInfo} from '@mescius/wijmo.grid';
import {WjGridUtils} from '../../lib/wjgrid-utils';
import {ColumnWithMetdata} from './account-grid-helper';
import {AccountDataWithChecks, CheckEvaluation, CheckSeverity, CheckUtils} from '../../data/app-data-checks';
import {CollectionView, Globalize} from '@mescius/wijmo';
import {WjGridChangeTracker} from '../../lib/wjgrid-change-tracker';
import {AccountData, AccountDataUpdateLog, Portfolio} from '../../../api-client-kt/src';
import {CancellableMouseOverWithDelay} from '../../components/component-tooltip/cancellable-mouse-over-with.delay';
import {NgDynamicComponentsService} from '../../service/ng-dynamic-components.service';
import {ComponentTooltip2} from '../../components/component-tooltip/component-tooltip2.service';
import {TooltipChecksComponent} from '../../components/tooltip-checks/tooltip-checks.component';
import {
    TooltipChangeConflictComponent
} from '../../components/tooltip-change-conflict/tooltip-change-conflict.component';
import {AccessControlService} from '../../security/access-control.service';
import {SegmentWithPermissions} from '../../data/portfolio-data-context.service';
import {
    AccountCommentsEditorComponent
} from '../../components/account-comments-editor/account-comments-editor.component';
import {TextTooltipComponent} from '../../components/component-tooltip/text-tooltip.component';
import {StringUtils} from '../../lib/strings';

/**
 * The class responsible for rendering and managing the behavior of grid cells within an account grid.
 * It handles custom formatting, change tracking, server-side updates, and tooltips for metadata checks
 * and comments.
 *
 * The class is designed to be used as a singleton within a single account grid.
 *
 */
export class AccountGridCellRenderer {

    private mouseOverHandler: CancellableMouseOverWithDelay | undefined = undefined;
    private componentTooltipContainer: ComponentTooltip2;

    constructor(
        private grid: FlexGrid,
        public gridHeaderBindingName: string,
        public gridHeaderBindingFormat: string,
        public segment: SegmentWithPermissions,
        public portfolio: Portfolio,
        private readonly gridChangeTracker: WjGridChangeTracker,
        private readonly serverSideAccountChanges: Map<string, AccountDataUpdateLog>,
        ngDynCompSvc: NgDynamicComponentsService,
        private accessControlSvc: AccessControlService
    ) {
        this.grid.formatItem.addHandler(this.accountCellsRowDataFormatHandler.bind(this));
        this.grid.beginningEdit.addHandler(this.beginEditHandler.bind(this));

        this.componentTooltipContainer = new ComponentTooltip2(ngDynCompSvc, {
            isResizable: true,
            isDraggable: true,
            maxWidth: '400px'
        });

        this.mouseOverHandler = new CancellableMouseOverWithDelay(this.grid.hostElement, {
             mouseOver: this.accountCellsMouseOverHandler.bind(this)
        });
    }

    public updateSegment(segment: SegmentWithPermissions) {
        this.segment = segment;
    }

    public unbindFromGrid() {
        this.mouseOverHandler?.destroy();
        this.mouseOverHandler = undefined;
    }

    private hasItemServerSideChanges(item: AccountData | undefined): boolean {
        if (!item?.id) return false;
        return this.serverSideAccountChanges.has(item.id);
    }

    private accountCellsRowDataFormatHandler(s: FlexGrid, e: FormatItemEventArgs) {
        const cell = e.cell;
        const isInputElement = WjGridUtils.hasAnyInputChildElement(cell);
        if (isInputElement) return;

        const col = e.getColumn() as ColumnWithMetdata;
        const row = s.rows[e.row];

        const item = s.collectionView?.items?.[e.row] as AccountDataWithChecks;
        if (!item) return;

        const isOutOfDate = this.hasItemServerSideChanges(item);
        const isLocallyModified = this.gridChangeTracker.hasChangeAnywhere(item);

        const isConflict = isOutOfDate && isLocallyModified;
        const isEditable = this.accessControlSvc.canEditAccount(item, this.segment, this.portfolio).allowed;

        cell.classList.toggle('is-out-of-date', isOutOfDate);
        cell.classList.toggle('is-conflict', isConflict);

        if (e.panel === s.columnHeaders) {
        } else if (e.panel === s.rowHeaders) {
            row.isReadOnly = isOutOfDate || !isEditable;

            const item = s.collectionView.items[e.row] as any;
            cell.innerText = Globalize.format(item[this.gridHeaderBindingName], this.gridHeaderBindingFormat);

        } else if (e.panel === s.cells) {
            const binding = col.binding!;
            const item = s.collectionView.items[e.row] as AccountDataWithChecks;
            if (!item) return;

            const canEditColumn = this.accessControlSvc.canEditAccountAttribute(item, binding, this.segment, this.portfolio).allowed;
            const isReadonly = isOutOfDate || !isEditable || col.isReadOnly || !canEditColumn;
            cell.classList.toggle('readonly', isReadonly);

            if (col.binding === 'errorLevel') {
                const severity = item.errorLevel;
                cell.innerHTML = CheckUtils.asFontAwesomeIconElement(severity) || '<i class="fa-solid fa-circle-check"></i>';
                cell.classList.add(`check-status-${severity ?? CheckSeverity.ok}-icon`);
            } else if (col.binding === 'status') {
                const accountStatus = item.status;
                cell.innerHTML = CheckUtils.accountStatusAsIcon(accountStatus);
                cell.classList.add(`account-status-${accountStatus}`);
            }

            if (col.groupCssClass) cell.classList.add(col.groupCssClass);

            const matches = CheckUtils.getChecksForAttribute(binding, item.metadataChecks as any);
            if (matches.length > 0) {
                const hasWarnings = matches.some((c: any) => c.severity === 'warning');
                const hasErrors = matches.some((c: any) => c.severity === 'error');
                cell.classList.toggle('check-status-warning', hasWarnings);
                cell.classList.toggle('check-status-error', hasErrors);
            }

            const comments = CheckUtils.getCommentsForAttribute(item, binding);
            if (comments.length > 0) {
                cell.classList.add('has-comments');
            }
        }
    }

    private beginEditHandler(s: FlexGrid, e: CellRangeEventArgs) {
        const cell = s.cells.getCellElement(e.row, e.col);
        if (cell.classList.contains('readonly')) {
            e.cancel = true;
        }
    }

    private async accountCellsMouseOverHandler(event: MouseEvent) {
        let ht: HitTestInfo | undefined;
        try { ht = this.grid.hitTest(event); }
        catch (e) {
            console.warn('Failed to hit test grid cell - possibly after Angular hot reload. Ignoring event');
        }
        if (!ht) return;

        const target = event.target as Element;
        const item = ht.getRow()?.dataItem as AccountDataWithChecks;

        if (!item) return;

        if (ht.panel === this.grid.cells) {
            const binding = this.grid.columns[ht.col]?.binding;
            const value = (item && binding) ? (item as any)[binding] : undefined;

            if (!binding) return;

            if (await this.showCommentsTooltipHandler(item, binding, target)) return;
            if (await this.showChecksTooltip(item, binding, target)) return;
            if (await this.showPricingCommentsTooltipHandler(item, binding, target)) return;
        } else if (ht.panel === this.grid.rowHeaders) {
            await this.showConflictsTooltipHandler(item, target);
        }
    }

    private async showChecksTooltip(item: AccountDataWithChecks, binding: string, target: Element): Promise<boolean> {
        let checks: CheckEvaluation[];
        if (binding === 'errorLevel') {
            checks = CheckUtils.getAccountChecks(item);
        } else {
            checks = CheckUtils.getChecksForAttribute(binding, item.metadataChecks as CheckEvaluation[]);
        }

        if (checks.length > 0) {
            await this.componentTooltipContainer.show(target, TooltipChecksComponent, {checks: structuredClone(checks)});
            return true;
        } else {
            return false;
        }
    }

    async showCommentsTooltipHandler (item: AccountDataWithChecks, binding: string, target: Element, force: boolean = false): Promise<boolean> {
        const comments = CheckUtils.getCommentsForAttribute(item, binding);
        const cv = this.grid.collectionView as CollectionView<AccountData>;

        if (force || comments.length > 0) {
            this.componentTooltipContainer.show(
                target, AccountCommentsEditorComponent, {
                    segment: this.segment,
                    account: item,
                    field: binding,
                    collectionView: cv,
                    autofocus: true
                },
            ).then(editor => {});
            return true;
        }
        return false;
    }

    private async showConflictsTooltipHandler(item: AccountDataWithChecks, target: Element): Promise<boolean> {
        const serverUpdateLog = this.serverSideAccountChanges.get(item.id!);
        const isConflict = this.gridChangeTracker.hasChangeAnywhere(item) && serverUpdateLog;
        if (serverUpdateLog) {
            await this.componentTooltipContainer.show(target, TooltipChangeConflictComponent, {
                changedBy: serverUpdateLog.updatedBy,
                changedAt: serverUpdateLog.updatedAt,
                isConflict: isConflict
            });
            return true;
        } else {
            return false;
        }
    }

    private async showPricingCommentsTooltipHandler(item: AccountDataWithChecks, binding: string, target: Element): Promise<boolean> {
        if (binding === 'comments') {
            const text = item.comments?.toString()?.trim();
            if (!StringUtils.isNullOrEmpty(text)) {
                await this.componentTooltipContainer.show(target, TextTooltipComponent, {
                    text: item.comments,
                });
            }
            return true;
        } else {
            return false;
        }
    }
}
